import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useStore } from 'jotai';
import { organizationDataAtom } from '@/store';
import { organizationsQueryOptions } from '@/lib/query-options';
import { clearAllUserData } from '@/lib/auth-cleanup';
import { OrganizationDataWithUsersMembership } from '@/lib/types';
import { useEffect, useState, useCallback } from 'react';
import { useUserProfile } from '@/contexts/user.context';

/**
 * Minimum time the "Switching organization…" overlay stays visible. An org switch
 * can resolve in well under 100ms (cached/local data), which makes the loader
 * appear as a jarring flash. Holding the veil for at least this long makes the
 * transition read as a deliberate, smooth change.
 */
const SWITCH_OVERLAY_MIN_MS = 500;

export function useOrganizationInitialization() {
  const queryClient = useQueryClient();
  const store = useStore();
  const [organizationData, setOrganizationData] = useAtom(organizationDataAtom);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { user } = useUserProfile();

  // Fetch all organizations
  const { data: organizations, isLoading } = useQuery(organizationsQueryOptions());

  // Keep the atom in sync with the latest org list data (e.g. logo URL changes after a refetch).
  useEffect(() => {
    if (!organizations || !organizationData) return;
    const fresh = organizations.find((o) => o.id === organizationData.id);
    if (fresh) setOrganizationData(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  // Initialize organization on first load
  useEffect(() => {
    if (organizations && organizations.length > 0 && !organizationData && !isInitialized) {
      let selectedOrg = null;
      
      // Use saved organization if available
      if (user?.last_organization_id) {
        selectedOrg = organizations.find(org => org.id === user.last_organization_id);
      }
      
      // Fallback to first organization
      if (!selectedOrg) {
        selectedOrg = organizations[0];
      }
      
      setOrganizationData(selectedOrg);
      setIsInitialized(true);
    }
  }, [organizations, organizationData, isInitialized, user?.last_organization_id, setOrganizationData]);

  // Secure organization switching function
  const switchOrganization = useCallback(async (newOrg: OrganizationDataWithUsersMembership) => {
    if (isSwitching) {
      return; // Prevent rapid switching
    }
    
    
    const switchStartedAt = Date.now();
    setIsSwitching(true);
    
    try {
      // First, persist selection to backend before clearing anything
      try {
        // Import apiEndpoint from consts and getCookie from auth
        const { apiEndpoint } = await import('@/lib/consts');
        const { getCookie } = await import('@/lib/auth');
        
        // Get token from cookie, not localStorage
        const token = getCookie('access_token');
        
        if (!token) {
          console.error('❌ [SWITCH] No access token available');
          throw new Error('No access token available');
        }
        
        const response = await fetch(`${apiEndpoint}/organizations/${newOrg.id}/select`, { 
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        

        if (!response.ok) {
          console.error('❌ [SWITCH] Failed to persist organization selection to backend');
        }
      } catch (error) {
        console.error('❌ [SWITCH] Failed to persist organization selection:', error);
        // Don't return early - still allow local switch even if backend fails
      }
      
      // Nuclear option: clear ALL data before switching
      clearAllUserData(queryClient, store);
      
      // Set the new organization
      setOrganizationData(newOrg);
      
      // Invalidate user profile query first to get updated last_organization_id
      await queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
      // Then invalidate all other queries
      await queryClient.invalidateQueries();
    } finally {
      const elapsed = Date.now() - switchStartedAt;
      if (elapsed < SWITCH_OVERLAY_MIN_MS) {
        await new Promise((resolve) => setTimeout(resolve, SWITCH_OVERLAY_MIN_MS - elapsed));
      }
      setIsSwitching(false);
    }
  }, [isSwitching, organizationData, queryClient, store, setOrganizationData]);

  return {
    organizations: organizations || [],
    organizationData,
    switchOrganization,
    isInitialized: isInitialized && !isLoading,
    isLoading: isLoading || isSwitching,
    isSwitching
  };
}